'use client'

import FaqAiPage from 'components/AiPage/FaqAiPage'
import ContactInformation from 'components/HomePage/ContactInformation'
import PageWrapper from 'components/PageWrapper'
import PAGE_CONSTANT_TEXTS from 'constants/pageMetaTags'
import AiBenefits from 'components/AiBenefits'
import AiIndustrySolutions from 'components/AiIndustrySolutions'
import AiIndustryTools from 'components/AiIndustryTools'
import ProvenChannelSection from 'components/AiInMarketingPage/ProvenChannel'
import BusinessGain from 'components/AiInMarketingPage/BusinessGain'
import FixStrategiesSection from 'components/AiInMarketingPage/FixStrategies'
import Differance from 'components/AiInMarketingPage/Differance'
import HeroSection from 'components/AiInMarketingPage/HeroSection'
import WhyGrowthSection from 'components/AiInMarketingPage/WhyGrowthSection'
import AiSecurityCtaSection from 'components/AiInMarketingPage/CtaSection'
import AdoptionSection from 'components/AiInMarketingPage/AdoptionSection'
import { heroSectionData } from 'constants/aiInMarketing/heroSection'
import {
  aiIndustrySolutionsData,
  aiIndustryToolsData,
  ProvenChannelData,
  NextWaveData,
  businessGainData,
  faqData,
  IntegrationData,
  AiMarketingBudgetData,
  AiMarketingSolutionsData,
  FixStrategiesData,
  DifferanceData,
  whyGrowthSectionData,
} from 'constants/aiInMarketing/aiInMarketingPageData'

export interface AiMarketingChallengeRow {
  challenge: string
  solution: string
}

export interface AiMarketingChallengesSectionData {
  titleOrange: string
  titleBlack: string
  subtitle: string
  headerChallenge: string
  headerSolution: string
  rows: AiMarketingChallengeRow[]
}

export const aiMarketingChallengesSectionData: AiMarketingChallengesSectionData =
  {
    titleOrange:
      'The Biggest Barriers to AI Marketing Adoption and How We Solve Them',
    titleBlack: '',
    subtitle:
      'AI marketing adoption can be complex, but using the right strategies and tools make implementation smooth and effective.',
    headerChallenge: 'AI Marketing Challenges',
    headerSolution: 'WebClues Solutions',
    rows: [
      {
        challenge: 'Data Silos',
        solution:
          'Unify CRM, analytics, social, and engagement data for a single, actionable view.',
      },
      {
        challenge: 'Skill Gaps',
        solution:
          'Provide hands-on training and strategic guidance to enable teams.',
      },
      {
        challenge: 'Integration Complexity',
        solution:
          'Ensure seamless integration across CRM, email, ads, and analytics platforms.',
      },
      {
        challenge: 'Budget & ROI Concerns',
        solution: 'Deploy AI in phases with measurable ROI tracking.',
      },
      {
        challenge: 'Trust & Accuracy',
        solution:
          'Use transparent models with continuous monitoring and optimization.',
      },
      {
        challenge: 'Change Management',
        solution:
          'Support adoption with step-by-step guidance and team enablement.',
      },
      {
        challenge: 'Data Privacy & Compliance',
        solution:
          'Ensure solutions are GDPR, CCPA, and locally compliant by design.',
      },
    ],
  }

const AiInMarketingPage = () => {
  return (
    <PageWrapper {...PAGE_CONSTANT_TEXTS['ai-in-marketing']}>
      <HeroSection data={heroSectionData} />

      <AiBenefits
        className="ai_marketing_budget"
        showIconBg={false}
        theme="light"
        sectionTitle={
          <div className="col-12 mb-lg-0">
            <h2 className="text-c-black fs-24px fs-lg-36px text-center">
              Why Your Marketing Budget Is Underperforming Without AI
            </h2>
          </div>
        }
        sectionDescription={
          <div className="col-12">
            <p className="text-c-black fs-18px fs-lg-20px mt-2 text-center">
              Marketing teams often struggle to get the most out of their
              budgets because traditional methods can&apos;t keep up with
              fast-moving digital landscape.
            </p>
          </div>
        }
        data={AiMarketingBudgetData}
        cardRow={false}
        columns={3}
        padding20
      />
      <FixStrategiesSection data={FixStrategiesData} />
      <AiBenefits
        className="ai_agent_benefit"
        showIconBg={false}
        theme="dark"
        sectionTitle={
          <div className="col-12 mb-lg-0">
            <h2 className="text-white fs-30px fs-lg-36px">
              AI Marketing Capabilities That Personalize, Target, and Convert at
              Scale
            </h2>
          </div>
        }
        sectionDescription={
          <div className="col-12">
            <p className="text-white fs-18px fs-lg-20px mt-2">
              WebClues Infotech equips teams with AI-powered tools and
              functionalities that make these solutions possible:
            </p>
          </div>
        }
        data={IntegrationData}
        cardRow={false}
        columns={3}
        padding20
      />

      <div className="bg-gray pt-lg-5">
        <Differance
          sectionTitle={
            <div className="col-12 mb-lg-0">
              <h2 className="text-c-black fs-30px fs-lg-36px text-center">
                Traditional Marketing vs AI-Driven Marketing: See the Difference
              </h2>
            </div>
          }
          sectionDescription={
            <div className="col-12">
              <p className="text-c-black fs-18px fs-lg-20px mt-2 text-center">
                Traditional marketing relies on manual processes, assumptions,
                and delayed insights, while AI-driven marketing
                <br className="d-none d-lg-block" /> transforms campaigns with
                real-time intelligence, automation, and personalization.
                Here&apos;s how they compare:
              </p>
            </div>
          }
          differanceData={DifferanceData}
        />
      </div>

      <ProvenChannelSection data={ProvenChannelData} />

      <AiIndustrySolutions data={aiIndustrySolutionsData} />

      <BusinessGain
        title={businessGainData.title}
        description={businessGainData.description}
        leftColumnItems={businessGainData.leftColumnItems}
      />

      <AiBenefits
        className="ai_marketing_budget"
        showIconBg={false}
        theme="light"
        sectionTitle={
          <div className="col-12 mb-lg-0">
            <h2 className="text-c-black fs-24px fs-lg-36px text-center">
              How We Implement AI Marketing Solutions From Audit to Launch
            </h2>
          </div>
        }
        sectionDescription={
          <div className="col-12">
            <p className="text-c-black fs-16px fs-lg-18px mt-2 text-center">
              How we bring AI marketing to life
            </p>
          </div>
        }
        data={AiMarketingSolutionsData}
        cardRow={false}
        columns={3}
        padding20
      />

      <AiIndustryTools data={aiIndustryToolsData} />

      <AdoptionSection data={aiMarketingChallengesSectionData} />

      <WhyGrowthSection data={whyGrowthSectionData} />

      <ProvenChannelSection
        titleClass="text-start"
        descriptionClass="text-start"
        data={NextWaveData}
        theme="dark"
      />

      <AiSecurityCtaSection />

      <FaqAiPage
        className="bg-white"
        items={faqData}
        title="Frequently Asked"
        titleorange="Questions"
        subtitle={
          <>
            Still have questions? <br className="d-none d-xxl-block" />
            Let’s talk.
          </>
        }
      />
      <ContactInformation
        data={{
          title: 'Regional Contact Info',
          subTitle: 'We can give you the Wings to Fly. Contact us now!',
        }}
      />
    </PageWrapper>
  )
}

export default AiInMarketingPage
